import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table/Table';
import MilestoneForm from '@components/ModalComponents/MilestoneForm';

import { useMutatePortalMilestones } from '@hooks/portal/milestones/useMutatePortalMilestones';
import { useQueryPortalMilestone } from '@hooks/portal/milestones/useQueryPortalMilestone';
import { useQueryPortalMilestones } from '@hooks/portal/milestones/useQueryPortalMilestones';
import {
	useQueryPortalMilestonesAssigneesFilter,
} from '@hooks/portal/milestones/useQueryPortalMilestonesAssigneesFilter';
import { useQueryPortalMilestonesStatuses } from '@hooks/portal/milestones/useQueryPortalMilestonesStatuses';
import { useQueryPortalAvailableProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalAvailableProjects';
import { useSearchParamsQuery } from '@hooks/useSearchParamsQuery';

import { portalMilestones as portalMilestonesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetPortalMilestones,
	setAssignees,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setStatus,
} from '@slices/portalMilestones';

import { setFilterUtil, setMultiSelectFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';

import { TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_772, modalCloseConfirm } from '@constants/modals';
import { TableFilterTypes } from '@constants/table';
import { portalSearchQueries } from '../constants';
import {
	deleteConfirmDescription,
	deleteConfirmText,
	searchTooltipText,
} from './constants';

import { getColumns } from './manageMilestonesColumns';

import styles from './Milestones.module.scss';

interface IMilestonesProps {
	projectId: string;
	isProjectActive: boolean;
	isPortalAdmin: boolean;
}

const Milestones = ({
	projectId,
	isProjectActive,
	isPortalAdmin,
}: IMilestonesProps) => {
	const dispatch = useAppDispatch();
	const query = useSearchParamsQuery();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const queryMilestoneId = query.get(portalSearchQueries.milestone);

	const removeSearchParams = () => {
		navigate(pathname, { replace: true });
	};

	const {
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		status,
		assignees,
	} = useAppSelector(portalMilestonesSelector);

	const [selectedMilestoneStatus, setSelectedMilestoneStatus] = useState<TSelectOption<string>>(null);
	const [selectedMilestoneAssignees, setSelectedMilestoneAssignees] = useState<TSelectOption<string>[] | null>(null);

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isViewMode, setIsViewMode] = useState(false);
	const [editableMilestoneId, setEditableMilestoneId] = useState<string | null>(null);
	const [isMilestoneFormModal, setIsMilestoneFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);

	const openDeleteConfirm = useCallback(() => {
		setIsShowDeleteConfirm(prevState => !prevState);
	}, []);

	const closeDeleteConfirm = useCallback(() => {
		setIsShowDeleteConfirm(false);
		setSingleIdForRemove(null);
	}, []);

	const openMilestoneFormModal = () => setIsMilestoneFormModal(true);

	const closeModals = () => {
		if (queryMilestoneId) removeSearchParams();

		setIsMilestoneFormModal(false);
		setIsCloseConfirmModal(false);
		setIsEditMode(false);
		setEditableMilestoneId(null);
	};

	const closeMilestoneViewModal = () => {
		if (queryMilestoneId) removeSearchParams();

		setIsViewMode(false);
		setEditableMilestoneId(null);
	};

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	useEffect(() => {
		if (!queryMilestoneId) return;

		setEditableMilestoneId(queryMilestoneId);

		if (isProjectActive) {
			setIsEditMode(true);
		} else {
			setIsViewMode(true);
		}
	}, [queryMilestoneId, isProjectActive]);

	const { portalAvailableProjects, isLoadingPortalAvailableProjects } = useQueryPortalAvailableProjects();
	const { portalMilestonesStatuses, isLoadingPortalMilestonesStatuses } = useQueryPortalMilestonesStatuses();

	const {
		portalMilestonesAssigneesFilter,
		isLoadingPortalMilestonesAssigneesFilter,
	} = useQueryPortalMilestonesAssigneesFilter(projectId);

	const {
		portalMilestones,
		isLoadingPortalMilestones,
		countOfRecords,
	} = useQueryPortalMilestones(projectId);

	const { portalMilestone, isFetchingPortalMilestone } = useQueryPortalMilestone(editableMilestoneId);

	const {
		createMilestone,
		updateMilestone,
		handleDeleteMilestones,
		handleDeleteMilestone,
		isLoadingDelete,
	} = useMutatePortalMilestones({
		projectId,
		status,
		assignees,
		searchValue,
		closeDeleteConfirm,
	});

	const columns = useMemo(() => getColumns({
		isProjectActive,
		isPortalAdmin,
		setIsEditMode,
		setEditableMilestoneId,
		setIsViewMode,
		openDeleteConfirm,
		setSingleIdForRemove,
	}), [openDeleteConfirm, isProjectActive, isPortalAdmin]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedMilestoneStatus,
				options: portalMilestonesStatuses,
				minWidth: '200px',
				setState: setSelectedMilestoneStatus,
				defaultValue: status,
			},
			{
				type: TableFilterTypes.MULTISELECT,
				name: 'assignees',
				placeholder: 'Assignee',
				option: selectedMilestoneAssignees,
				options: portalMilestonesAssigneesFilter,
				minWidth: '200px',
				setState: setSelectedMilestoneAssignees,
				defaultValue: assignees,
			},
		];
	}, [
		portalMilestonesStatuses,
		selectedMilestoneStatus,
		status,
		assignees,
		portalMilestonesAssigneesFilter,
		selectedMilestoneAssignees,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedMilestoneStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: 'status',
			dispatch,
		});
	}, [dispatch, status, selectedMilestoneStatus]);

	useEffect(() => {
		setMultiSelectFilterUtil({
			selectedValues: selectedMilestoneAssignees,
			setAction: setAssignees,
			fieldName: 'assignees',
			dispatch,
		});
	}, [dispatch, selectedMilestoneAssignees]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, [openDeleteConfirm]);

	const deleteItemsHandler = useCallback(() => {
		if (singleIdForRemove) return handleDeleteMilestone(singleIdForRemove);

		return handleDeleteMilestones({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeleteMilestone,
		handleDeleteMilestones,
		singleIdForRemove,
	]);

	const isLoading = isLoadingPortalMilestones
		|| isLoadingPortalAvailableProjects
		|| isLoadingPortalMilestonesStatuses
		|| isLoadingPortalMilestonesAssigneesFilter
		|| isFetchingPortalMilestone;

	const defaultProject = (portalAvailableProjects && !isEditMode && !isViewMode
		? portalAvailableProjects.find(project => project?.value === projectId)
		: undefined) as TSelectOption<string>;

	useEffect(() => {
		return () => {
			dispatch(resetPortalMilestones());
		};
	}, [dispatch]);

	return (
		<div className={styles.milestonesTableContainer}>
			<Table
				columns={columns}
				data={portalMilestones}
				filters={filters}
				handleCustomDelete={handleCustomDelete}
				isCreatable={isProjectActive}
				isDeleteActionDisabled={!isProjectActive}
				isLoading={isLoading}
				isPinColumns
				isWithoutCheckbox={!isPortalAdmin}
				isWithoutDeleteAction={!isPortalAdmin}
				maxHeight='55vh'
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={[0, 1, 2]}
				toggleAddModal={openMilestoneFormModal}
			/>
			<Modal
				isDirtyForm={isDirtyFormModal}
				isOpen={isMilestoneFormModal}
				onClose={toggleCloseConfirmModal}
				title='Add milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeModals}
					createMilestone={createMilestone}
					defaultProject={defaultProject}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>

			<Modal
				isDirtyForm={isDirtyFormModal}
				isOpen={isEditMode && !isLoading}
				onClose={toggleCloseConfirmModal}
				title='Edit milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeModals}
					isEditMode={isEditMode}
					portalMilestone={portalMilestone}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
					updateMilestone={updateMilestone}
				/>
			</Modal>

			<Modal
				isOpen={isViewMode && !isLoading}
				onClose={closeMilestoneViewModal}
				title='View milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeMilestoneViewModal}
					isViewMode={isViewMode}
					portalMilestone={portalMilestone}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>

			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={toggleCloseConfirmModal}
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDelete}
					onSubmit={deleteItemsHandler}
					question={deleteConfirmText}
				>
					{deleteConfirmDescription}
				</Confirm>
			</Modal>
		</div>
	);
};

export default Milestones;
